import { useCallback, useMemo, useState } from 'react'
import { ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native'
import { Dialog, Icon, Modal, Portal, Text, useTheme } from 'react-native-paper'
import { useAdminAddTimeEntry } from '@/hooks/useAdminAddTimeEntry'
import { useAdminEditTimeEntry } from '@/hooks/useAdminEditTimeEntry'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useDismissOnEscape } from '@/hooks/useDismissOnEscape'
import { useUpdateTimeEntry } from '@/hooks/useUpdateTimeEntry'
import { useAuth } from '@/lib/auth'
import { BREAKPOINTS } from '@/lib/config'
import { calculateDuration, formatDuration, parseDate } from '@/lib/dates'
import { getDeviceLocation } from '@/lib/device-location'
import { captureException } from '@/lib/errors'
import { log } from '@/lib/logger'
import { offlineExecutor } from '@/lib/offline'
import { newRecordId, useStore } from '@/lib/pocketbase'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import { Button } from '@/ui/Button'
import { DismissKeyboardView } from '@/ui/DismissKeyboardView'
import { DateTimeInput, TextAreaInput, Toggle, useForm, type z, zodResolver } from '@/ui/form'
import { JobPicker } from '@/ui/time-entry/JobPicker'
import { LocationPicker } from '@/ui/time-entry/LocationPicker'
import { buildManualEntrySchema } from '@/ui/time-entry/manualEntrySchema'

type ManualEntryFormData = z.infer<ReturnType<typeof buildManualEntrySchema>>

interface ManualEntryPrefill {
    clockIn: Date
    clockOut: Date
    jobId: string
    locationId: string
}

interface ManualEntryDialogProps {
    visible: boolean
    orgSlug: string
    onDismiss: () => void
    entry?: TimeEntriesExpanded | null
    // The actor is privileged (admin/clerical) and may be acting on another
    // user's entry: bypasses entry locks, uses the admin edit/create mutations,
    // and approves on save. Note: the note-required rule does NOT key off this —
    // it follows the current user's own role (see notesRequired below) so an
    // admin editing their own entry also skips the note.
    isPrivilegedAction?: boolean
    targetUserId?: string
    targetUserName?: string
    entriesLockedUntil?: string | null
    prefill?: ManualEntryPrefill | null
    entriesToDeleteOnSave?: string[]
    consolidationNote?: string
    originalTotalSeconds?: number
}

export function ManualEntryDialog({ visible, ...props }: ManualEntryDialogProps) {
    if (!visible) return null
    return <ManualEntryDialogContent {...props} />
}

function LockedPeriodWarning({ isVisible }: { isVisible: boolean }) {
    if (!isVisible) return null

    return (
        <View style={styles.lockedWarning}>
            <Text variant="bodySmall" style={styles.lockedWarningText}>
                This period is locked for other users
            </Text>
        </View>
    )
}

function MergeTimeDifferenceAlert({
    clockIn,
    clockOut,
    originalTotalSeconds,
}: {
    clockIn: Date | null
    clockOut: Date | null
    originalTotalSeconds?: number
}) {
    const message = useMemo(() => {
        if (!clockIn || !clockOut || !originalTotalSeconds || clockOut <= clockIn) return null
        const mergedSeconds = calculateDuration({
            clock_in: clockIn.toISOString(),
            clock_out: clockOut.toISOString(),
        })
        const diff = mergedSeconds - originalTotalSeconds
        if (diff === 0) return null
        if (diff > 0) return `Merging will add ${formatDuration(diff)} to this day`
        return `Merging will remove ${formatDuration(Math.abs(diff))} from this day`
    }, [clockIn, clockOut, originalTotalSeconds])

    if (!message) return null

    return (
        <View style={styles.mergeAlert}>
            <Icon source="information-outline" size={16} color="#1565c0" />
            <Text variant="bodySmall" style={styles.mergeAlertText}>
                {message}
            </Text>
        </View>
    )
}

function deleteConsolidatedEntries(
    entryIds: string[],
    collection: { delete: (id: string) => void }
) {
    const tx = offlineExecutor.createOfflineTransaction({
        mutationFnName: 'syncTimeEntries',
    })
    tx.mutate(() => {
        for (const id of entryIds) {
            collection.delete(id)
        }
    })
}

function ManualEntryDialogContent({
    orgSlug,
    onDismiss,
    entry,
    isPrivilegedAction,
    targetUserId,
    targetUserName,
    entriesLockedUntil,
    prefill,
    entriesToDeleteOnSave,
    consolidationNote,
    originalTotalSeconds,
}: Omit<ManualEntryDialogProps, 'visible'>) {
    const { user } = useAuth()
    const theme = useTheme()
    const { org: currentOrg, role } = useCurrentOrg({
        throwIfNotFound: false,
        onlySlug: orgSlug,
    })

    // Only regular workers must justify a manual entry with a note; admin and
    // clerical staff do not, regardless of whose entry they're editing or which
    // screen launched the dialog. Keyed off the current user's role, not the
    // privileged-action prop, so an admin editing their OWN entry also skips it.
    const notesRequired = role === 'workforce'
    const [timeEntriesCollection] = useStore('time_entries')
    const updateTimeEntry = useUpdateTimeEntry()
    const adminEditTimeEntry = useAdminEditTimeEntry()
    const adminAddTimeEntry = useAdminAddTimeEntry()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const dismissDeleteConfirm = useCallback(() => setShowDeleteConfirm(false), [])
    useDismissOnEscape(true, onDismiss)
    useDismissOnEscape(showDeleteConfirm, dismissDeleteConfirm)
    const { width, height } = useWindowDimensions()
    const isDesktop = width >= BREAKPOINTS.lg

    const mode = entry ? 'edit' : 'create'

    const defaultValues = useMemo(() => {
        if (entry) {
            const clockInDate = parseDate(entry.clock_in)
            const clockOutDate = entry.clock_out ? parseDate(entry.clock_out) : new Date()

            return {
                jobId: entry.job,
                locationId: entry.location || '',
                clockIn: clockInDate,
                clockOut: clockOutDate,
                notes: entry.notes || '',
                isBreak: entry.entry_type === 'break',
            }
        }

        if (prefill) {
            return {
                jobId: prefill.jobId,
                locationId: prefill.locationId,
                clockIn: prefill.clockIn,
                clockOut: prefill.clockOut,
                notes: consolidationNote || '',
                isBreak: false,
            }
        }

        // Default to today 9am-5pm
        const today = new Date()
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 9, 0)
        const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 17, 0)

        return {
            jobId: '',
            locationId: '',
            clockIn: todayStart,
            clockOut: todayEnd,
            notes: '',
            isBreak: false,
        }
    }, [entry, prefill, consolidationNote])

    const schema = useMemo(() => buildManualEntrySchema(notesRequired), [notesRequired])

    const {
        control,
        handleSubmit,
        formState: { isValid },
        watch,
    } = useForm<ManualEntryFormData>({
        resolver: zodResolver(schema),
        defaultValues,
        mode: 'onChange',
    })

    const jobId = watch('jobId')
    const clockIn = watch('clockIn')
    const clockOut = watch('clockOut')

    const duration = useMemo(() => {
        if (!clockIn || !clockOut) return null
        try {
            if (clockOut <= clockIn) return null

            const durationSeconds = calculateDuration({
                clock_in: clockIn.toISOString(),
                clock_out: clockOut.toISOString(),
            })

            return formatDuration(durationSeconds)
        } catch {
            return null
        }
    }, [clockIn, clockOut])

    const isConsolidation = !!entriesToDeleteOnSave?.length

    const dialogTitle = useMemo(() => {
        if (isConsolidation && targetUserName) {
            return `Merge Entries for ${targetUserName}`
        }
        if (isPrivilegedAction && mode === 'create' && targetUserName) {
            return `Add Entry for ${targetUserName}`
        }
        if (isPrivilegedAction && mode === 'edit' && targetUserName) {
            return `Edit Entry for ${targetUserName}`
        }
        return mode === 'edit' ? 'Edit Time Entry' : 'Manual Time Entry'
    }, [isPrivilegedAction, isConsolidation, mode, targetUserName])

    const saveButtonTitle = isConsolidation
        ? 'Merge'
        : isPrivilegedAction
          ? 'Save & Approve'
          : 'Save'

    const lockDate = entriesLockedUntil ? new Date(entriesLockedUntil) : undefined
    const clockInMinimumDate = !isPrivilegedAction && lockDate ? lockDate : undefined

    const showLockedWarning =
        isPrivilegedAction &&
        !!entriesLockedUntil &&
        clockIn &&
        clockIn.toISOString() < entriesLockedUntil

    const buildEntryFields = (data: ManualEntryFormData) => ({
        job: data.jobId,
        location: data.locationId || '',
        clock_in: data.clockIn.toISOString(),
        clock_out: data.clockOut.toISOString(),
        duration: calculateDuration({
            clock_in: data.clockIn.toISOString(),
            clock_out: data.clockOut.toISOString(),
        }),
        notes: data.notes || '',
        entry_type: (data.isBreak ? 'break' : 'manual') as 'manual' | 'break',
    })

    const submitAdminCreate = async (
        entryFields: ReturnType<typeof buildEntryFields>,
        orgId: string
    ) => {
        if (!targetUserId) return
        await adminAddTimeEntry.mutateAsync({
            targetUserId,
            orgId,
            ...entryFields,
        })
        if (entriesToDeleteOnSave?.length) {
            deleteConsolidatedEntries(entriesToDeleteOnSave, timeEntriesCollection)
        }
    }

    const submitEntry = async (data: ManualEntryFormData, orgId: string, userId: string) => {
        const entryFields = buildEntryFields(data)

        if (isPrivilegedAction && mode === 'edit' && entry) {
            return adminEditTimeEntry.mutateAsync({
                entryId: entry.id,
                orgId,
                previousEntry: entry,
                ...entryFields,
            })
        }
        if (isPrivilegedAction && mode === 'create') {
            return submitAdminCreate(entryFields, orgId)
        }
        if (mode === 'edit' && entry) {
            return updateTimeEntry.mutateAsync({
                entryId: entry.id,
                orgId,
                previousEntry: entry,
                ...entryFields,
            })
        }

        const entryId = newRecordId()
        const tx = offlineExecutor.createOfflineTransaction({
            mutationFnName: 'syncTimeEntries',
        })
        tx.mutate(() => {
            timeEntriesCollection.insert({
                id: entryId,
                user: userId,
                org: orgId,
                ...entryFields,
                status: 'pending',
                approved_by: '',
                approved_at: '',
                modified_by: '',
                rejection_reason: '',
            })
        })

        getDeviceLocation()
            .then(location => {
                if (!location) return
                const locationTx = offlineExecutor.createOfflineTransaction({
                    mutationFnName: 'syncTimeEntries',
                })
                locationTx.mutate(() => {
                    timeEntriesCollection.update(entryId, draft => {
                        draft.clock_in_location = location
                    })
                })
                log.debug('Manual entry location captured', location)
            })
            .catch(() => {})
    }

    const onSubmit = async (data: ManualEntryFormData) => {
        if (!user?.id || !currentOrg?.id) return

        setIsSubmitting(true)
        try {
            await submitEntry(data, currentOrg.id, user.id)
            onDismiss()
        } catch (error) {
            captureException('Manual time entry failed', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const onDeleteEntry = async () => {
        if (!entry) return

        setIsDeleting(true)
        try {
            const tx = offlineExecutor.createOfflineTransaction({
                mutationFnName: 'syncTimeEntries',
            })
            tx.mutate(() => {
                timeEntriesCollection.delete(entry.id)
            })
            setShowDeleteConfirm(false)
            onDismiss()
        } catch (error) {
            captureException('Delete time entry failed', error)
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <Portal>
            <Modal
                visible
                onDismiss={onDismiss}
                contentContainerStyle={[
                    styles.modalContainer,
                    {
                        backgroundColor: theme.colors.surface,
                        width: isDesktop ? 500 : width - 40,
                        maxHeight: height * 0.85,
                    },
                ]}
            >
                <DismissKeyboardView style={styles.avoidWrapperStyles}>
                    <View style={styles.header}>
                        <Text
                            variant={isConsolidation ? 'titleMedium' : 'titleLarge'}
                            style={styles.title}
                            numberOfLines={1}
                        >
                            {dialogTitle}
                        </Text>
                        <Button title="✕" onPress={onDismiss} mode="text" />
                    </View>
                    <ScrollView
                        style={styles.scrollView}
                        contentContainerStyle={styles.content}
                        keyboardShouldPersistTaps="handled"
                    >
                        <LockedPeriodWarning isVisible={!!showLockedWarning} />

                        <JobPicker orgSlug={orgSlug} name="jobId" control={control} />

                        {jobId && (
                            <LocationPicker name="locationId" control={control} jobId={jobId} />
                        )}

                        <View style={styles.fieldsRow}>
                            <DateTimeInput
                                name="clockIn"
                                control={control}
                                label="Clock In"
                                mode="datetime"
                                minimumDate={clockInMinimumDate}
                                wrapperProps={{
                                    style: isDesktop
                                        ? styles.desktopQuarterField
                                        : styles.mobileFullField,
                                }}
                            />

                            <DateTimeInput
                                name="clockOut"
                                control={control}
                                label="Clock Out"
                                mode="datetime"
                                wrapperProps={{
                                    style: isDesktop
                                        ? styles.desktopQuarterField
                                        : styles.mobileFullField,
                                }}
                            />
                        </View>

                        <View style={styles.breakDurationRow}>
                            <Toggle
                                name="isBreak"
                                control={control}
                                label="Break"
                                wrapperProps={{ style: styles.breakToggle }}
                            />
                            <View style={styles.durationDisplay}>
                                <Text variant="labelSmall" style={styles.durationLabel}>
                                    Duration
                                </Text>
                                <Text variant="titleMedium" style={styles.durationValue}>
                                    {duration}
                                </Text>
                            </View>
                        </View>

                        <TextAreaInput
                            name="notes"
                            control={control}
                            label="What are you working on?"
                            numberOfLines={3}
                        />

                        <MergeTimeDifferenceAlert
                            clockIn={clockIn}
                            clockOut={clockOut}
                            originalTotalSeconds={originalTotalSeconds}
                        />

                        <View style={[styles.actions, mode === 'edit' && styles.actionsWithDelete]}>
                            {mode === 'edit' && (
                                <Button
                                    title="Delete"
                                    onPress={() => setShowDeleteConfirm(true)}
                                    color={theme.colors.error}
                                />
                            )}
                            <View style={styles.actionsRight}>
                                <Button title="Cancel" onPress={onDismiss} />
                                <Button
                                    title={saveButtonTitle}
                                    onPress={handleSubmit(onSubmit)}
                                    disabled={!isValid}
                                    loading={isSubmitting}
                                    loadingTitle="Saving..."
                                />
                            </View>
                        </View>
                    </ScrollView>
                </DismissKeyboardView>
            </Modal>
            <Dialog visible={showDeleteConfirm} onDismiss={() => setShowDeleteConfirm(false)}>
                <Dialog.Title>Delete Entry</Dialog.Title>
                <Dialog.Content>
                    <Text>
                        Are you sure you want to delete this time entry? This action cannot be
                        undone.
                    </Text>
                </Dialog.Content>
                <Dialog.Actions>
                    <Button
                        title="Cancel"
                        onPress={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                    />
                    <Button
                        title="Delete"
                        onPress={onDeleteEntry}
                        loading={isDeleting}
                        disabled={isDeleting}
                        color={theme.colors.error}
                    />
                </Dialog.Actions>
            </Dialog>
        </Portal>
    )
}

const styles = StyleSheet.create({
    modalContainer: {
        margin: 20,
        justifyContent: 'flex-start',
        borderRadius: 12,
        alignSelf: 'center',
    },
    avoidWrapperStyles: {
        flexShrink: 1,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 24,
        paddingTop: 20,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(0, 0, 0, 0.1)',
    },
    title: {
        flex: 1,
    },
    scrollView: {
        flexGrow: 1,
    },
    content: {
        paddingHorizontal: 24,
        paddingBottom: 24,
        gap: 16,
    },
    fieldsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'flex-end',
        zIndex: 10,
    },
    desktopQuarterField: {
        flex: 1,
        minWidth: 0,
    },
    mobileFullField: {
        width: '100%',
    },
    breakDurationRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    breakToggle: {
        marginBottom: 0,
    },
    durationDisplay: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
        minWidth: 80,
        alignItems: 'flex-end',
    },
    durationLabel: {
        opacity: 0.7,
        marginBottom: 2,
    },
    durationValue: {
        fontWeight: '600',
    },
    actions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 8,
    },
    actionsWithDelete: {
        justifyContent: 'space-between',
    },
    actionsRight: {
        flexDirection: 'row',
        gap: 8,
    },
    lockedWarning: {
        backgroundColor: 'rgba(255, 152, 0, 0.1)',
        borderRadius: 8,
        padding: 12,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    lockedWarningText: {
        color: '#e65100',
        fontWeight: '500',
    },
    mergeAlert: {
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        borderRadius: 8,
        padding: 12,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    mergeAlertText: {
        color: '#1565c0',
        fontWeight: '500',
    },
})
